import React from "react";
import Layout from "../../components/Layout";
import OpenGraphTitle from "../../components/OpenGraphTitle";
import OpenGraphDescription from "../../components/OpenGraphDescription";
import Head from "../../components/head";
import { ServicesPageProvider } from "../../context/ServicesPageContext";
import { FranchiseeProvider } from "../../context/Franchisee";
import ServiceTitle from "../../components/ServiceTitle";
import ServicesDescription from "../../components/ServicesDescription";
import { Col, Row } from "react-bootstrap";
import Link from "next/link";
import ContactUs from "../../components/ContactUs";

interface ApartmentMoveOutPageProps {
  /* add services page props here */
}

const ApartmentMoveOut: React.FC<ApartmentMoveOutPageProps> = () => (
  <FranchiseeProvider>
    <ServicesPageProvider>
      <Layout alternateFooter={true}>
        <Head
          slug="services"
          title="Recurring or One Time Cleaning Services - Two Maids"
          description="Two Maids offers a range of house cleaning services from one time as needed, to regular maintenance."
        />
        <OpenGraphTitle title="Recurring or One Time Cleaning Services - Two Maids" />

        <OpenGraphDescription>
          Two Maids offers a range of house cleaning services from one time as needed, to regular maintenance.
        </OpenGraphDescription>

        <ServiceTitle imageSrc="/static/design-with-time.png">Apartment Move-Out</ServiceTitle>
        <div className="service-description gray-background">
          <Row className="pb-3">
            <Col md={12} lg={5}>
              <h2>Apartment Move-Out Cleaning Services</h2>
              <h3 className="service-description-title">Making Cleaning Ones Less Thing to Worry About</h3>
              <p>
                Moving can be an overwhelming process, filled with endless tasks and details – and moving out of an
                apartment is no less stressful than moving out of a house. At Two Maids, we offer apartment move-out
                cleaning to help ease the burden by taking care of cleaning during your move.
              </p>
              <p>
                We understand that moving out of your apartment often means you need a thorough yet efficient clean.
                Our services with our cleaning professionals ensure that your apartment will be left refreshed as
                quickly as possible. So take a deep breath, and let us handle the deep clean you need while moving
                out of your apartment.
              </p>
            </Col>
            <Col md={12} lg={7}>
              <h3 className="service-description-title gotham-bold">Comprehensive Home Cleaning for Every Room</h3>
              <p className="service-description-title">
                <i>
                  Cleaning apartments for a move-out is different from an everyday clean, and requires more intensive
                  cleaning measures. Getting an apartment thoroughly cleaned by Two Maids is beneficial because:
                </i>
              </p>
              <ul>
                <li>
                  <b className="gotham-bold">We help make sure you’ll get your deposit return:</b> To get your security deposit back, your previous
                  apartment typically needs to be thoroughly cleaned. Our services guarantee that every nook and cranny is cleaned
                  to meet the highest standards.
                </li>
                <li>
                  <b className="gotham-bold">We’ll accommodate any apartment layout:</b> Apartments, often smaller than houses, require space-efficient
                  cleaning techniques and equipment. We tailor our services to handle compact layouts effectively.
                </li>
                <li>
                  <b className="gotham-bold">We customize our services:</b> Each apartment has unique features. We customize our cleaning approach to
                  ensure every aspect of your space is addressed.
                </li>
                <li>
                  <b className="gotham-bold">We’ll save you time:</b> SOur professionals are experts. They’ll be able to clean your apartment quickly
                  and thoroughly with cleaning methods and equipment you may not have access to. That way, you can get on with
                  handling the rest of your move without worrying about the clean up.
                </li>
              </ul>
            </Col>
          </Row>
        </div>
        <ServicesDescription colSize={12} title="What Does Our Apartment Move-Out Cleaning Service Include?">
          <p>
            Our Empty Home Move Out Cleaning Package is designed to make the move-out process easy. To ensure the highest quality
            of service, please remove all personal belongings prior to cleaning. Our move out services include:
          </p>
        </ServicesDescription>
        <ServicesDescription colSize={12} className="gray-background">
          <h3 className="service-description-title">Kitchen Cleaning</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean inside/outside of microwave, oven, and refrigerator
                </li>
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  Clean windows, windowsills, and blinds
                </li>
                <li>
                  Clean backsplash
                </li>
                <li>
                  Sweep and mop floors
                </li>
                <li>
                  Clean the outside of the dishwasher
                </li>
                <li>
                  Clean stovetop
                </li>
                <li>
                  High dust ceiling fans, light fixtures, air vents and cobwebs
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean baseboards
                </li>
                <li>
                  Clean countertops
                </li>
                <li>
                  Clean sink
                </li>
                <li>
                  Clean door and doorframes
                </li>
                <li>
                  Clean inside/outside of cabinets and drawers
                </li>
                <li>
                  Clean pantry shelves, floor, and door
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription colSize={12}>
          <h3 className="service-description-title">Bedrooms</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean windows, windowsills, and blinds
                </li>
                <li>
                  Sweep and mop floors
                </li>
                <li>
                  High dust ceiling fans, light fixtures, air vents and cobwebs
                </li>
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  Clean shelves
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean baseboards
                </li>
                <li>
                  Vacuum carpets
                </li>
                <li>
                  Clean doors and doorframes
                </li>
                <li>
                  Clean inside and outside of closets
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription colSize={12} className="gray-background">
          <h3 className="service-description-title">Common Areas:</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  High dust ceiling fans, light fixtures, air vents and cobwebs
                </li>
                <li>
                  Sweep and mop floors
                </li>
                <li>
                  Clean counters
                </li>
                <li>
                  Clean shelves
                </li>
                <li>
                  Clean inside/outside of cabinets and drawers
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Vacuum carpets and rugs
                </li>
                <li>
                  Clean windows, windowsills, and blinds
                </li>
                <li>
                  Clean doors and doorframes
                </li>
                <li>
                  Clean baseboards
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription colSize={12}>
          <h3 className="service-description-title">Bathrooms:</h3>
          <Row className="pb-3">
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean shower and bathtubs
                </li>
                <li>
                  Polish faucets
                </li>
                <li>
                  Clean mirrors and glass surfaces
                </li>
                <li>
                  Sweep and mop floors
                </li>
                <li>
                  Clean toilets
                </li>
                <li>
                  Clean baseboards
                </li>
                <li>
                  Clean counters
                </li>
              </ul>
            </Col>
            <Col sm={12} md={6}>
              <ul className="services-description-list">
                <li>
                  Clean doors and doorframes
                </li>
                <li>
                  Clean sink
                </li>
                <li>
                  High dust fans, air vents, and light fixtures
                </li>
                <li>
                  Clean inside/outside of cabinets and drawers
                </li>
                <li>
                  Clean windows, windowsills, and blinds
                </li>
                <li>
                  Clean inside and outside of closets
                </li>
                <li>
                  Clean light switch and outlet covers
                </li>
              </ul>
            </Col>
          </Row>
        </ServicesDescription>
        <ServicesDescription colSize={12} className="gray-background" title="Why Two Maids?">
          <p>
            Your apartment will receive the highest level of care from our fully licensed, bonded, and insured teams. 
            We offer customizable services to meet your specific needs.
          </p>
          <p>
            In addition to providing the right package for you, we are committed to sending only our trained cleaning 
            professionals, who are passionate about their work and dedicated to excellence. At Two Maids, we foster a 
            culture of compassion, interconnectedness, and grit, creating a welcoming and supportive environment for 
            both our team and clients.
          </p>
        </ServicesDescription>
        <ContactUs
          title="React Out for Us"
          buttons={
            <Link href="/book">
              <a className="contact-us-button">BOOK AN APPOINTMENT</a>
            </Link>
          }
          className="mt-5"
        >
          <p>
            Moving doesn’t have to be stressful. Incorporating our professional cleaning services into your plan can 
            make a significant difference. Let Two Maids handle your move-out cleaning needs, ensuring a fresh, clean 
            home ready for the next occupants.
          </p>
        </ContactUs>
      </Layout>
    </ServicesPageProvider>
  </FranchiseeProvider>
);
export default ApartmentMoveOut;